import React from 'react';
import { FiCheckCircle, FiAlertTriangle, FiXCircle, FiInfo, FiX } from 'react-icons/fi';

interface ReportSection {
  title: string;
  status: string;
  summary_metrics: Record<string, any>;
  tables: Array<{
    name: string;
    data: any[];
  }>;
  notes?: string;
  data_quality?: {
    window_start: string;
    window_end: string;
    n_samples: number;
    last_event_ts: string;
    age_of_last_event_seconds: number;
    data_freshness_status: string;
  };
}

interface SystemHealthReport {
  report_type: string;
  report_name: string;
  environment: string;
  generated_at: string;
  git_sha: string;
  version: string;
  sections: ReportSection[];
  overall_status: string;
  classification: string;
  pii_risk: string;
  global_metrics?: {
    last_processed_webhook_ts?: string;
    last_successful_handler_ts?: string;
    time_since_last_success_seconds?: number;
  };
}

interface Props {
  report: SystemHealthReport;
  onClose: () => void;
}

const SystemHealthReportViewer: React.FC<Props> = ({ report, onClose }) => {
  // Get status icon and color
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'OK':
        return <FiCheckCircle className="text-green-600" />;
      case 'WARN':
        return <FiAlertTriangle className="text-yellow-600" />;
      case 'CRIT':
        return <FiXCircle className="text-red-600" />;
      default:
        return <FiInfo className="text-blue-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OK': return 'bg-green-100 text-green-800 border-green-200';
      case 'WARN': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'CRIT': return 'bg-red-100 text-red-800 border-red-200';
      case 'NO_DATA': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'STALE_DATA': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'INSUFFICIENT_DATA': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Format timestamp
  const formatTimestamp = (ts: string) => {
    return new Date(ts).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  // Format duration
  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-start z-10">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
              System Health Report
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(report.overall_status)}`}>
                {report.overall_status}
              </span>
            </h1>
            <div className="mt-2 text-sm text-slate-600 space-y-1">
              <p><strong>Environment:</strong> {report.environment.toUpperCase()}</p>
              <p><strong>Generated:</strong> {formatTimestamp(report.generated_at)}</p>
              <p><strong>Version:</strong> {report.version} | <strong>Git SHA:</strong> {report.git_sha}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Global Metrics */}
        {report.global_metrics && (
          <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Global Metrics</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {report.global_metrics.last_processed_webhook_ts && (
                <div>
                  <p className="text-xs text-slate-500 uppercase">Last Processed Webhook</p>
                  <p className="text-sm font-medium text-slate-800">{formatTimestamp(report.global_metrics.last_processed_webhook_ts)}</p>
                </div>
              )}
              {report.global_metrics.last_successful_handler_ts && (
                <div>
                  <p className="text-xs text-slate-500 uppercase">Last Successful Handler</p>
                  <p className="text-sm font-medium text-slate-800">{formatTimestamp(report.global_metrics.last_successful_handler_ts)}</p>
                </div>
              )}
              {report.global_metrics.time_since_last_success_seconds !== undefined && (
                <div>
                  <p className="text-xs text-slate-500 uppercase">Time Since Last Success</p>
                  <p className="text-sm font-medium text-slate-800">{formatDuration(report.global_metrics.time_since_last_success_seconds)}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sections */}
        <div className="px-6 py-4 space-y-6">
          {report.sections.map((section, idx) => (
            <div key={idx} className="border border-slate-200 rounded-lg overflow-hidden">
              {/* Section Header */}
              <div className={`px-4 py-3 border-b border-slate-200 flex items-center gap-3 ${getStatusColor(section.status)}`}>
                {getStatusIcon(section.status)}
                <h3 className="font-semibold text-base">{section.title}</h3>
              </div>

              {/* Summary Metrics */}
              {section.summary_metrics && Object.keys(section.summary_metrics).length > 0 && (
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Summary</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {Object.entries(section.summary_metrics).map(([key, value]) => (
                      <div key={key}>
                        <p className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</p>
                        <p className="text-sm font-medium text-slate-800">{String(value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tables */}
              {section.tables && section.tables.length > 0 && section.tables.map((table, tableIdx) => (
                <div key={tableIdx} className="px-4 py-3">
                  {table.name && <h4 className="text-sm font-medium text-slate-700 mb-2">{table.name}</h4>}
                  {table.data && table.data.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead className="bg-slate-100">
                          <tr>
                            {Object.keys(table.data[0]).map((key) => (
                              <th key={key} className="px-3 py-2 text-left text-xs font-medium text-slate-600 uppercase">
                                {key.replace(/_/g, ' ')}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {table.data.map((row, rowIdx) => (
                            <tr key={rowIdx} className="hover:bg-slate-50">
                              {Object.values(row).map((value, cellIdx) => (
                                <td key={cellIdx} className="px-3 py-2 text-slate-800">
                                  {String(value)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 italic">No data available</p>
                  )}
                </div>
              ))}

              {/* Notes */}
              {section.notes && (
                <div className="px-4 py-3 bg-blue-50 border-t border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> {section.notes}
                  </p>
                </div>
              )}

              {/* Data Quality */}
              {section.data_quality && (
                <div className="px-4 py-3 bg-slate-50 border-t border-slate-200">
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Data Quality</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                    <div>
                      <p className="text-slate-500">Window</p>
                      <p className="text-slate-800 font-medium">
                        {formatTimestamp(section.data_quality.window_start)} - {formatTimestamp(section.data_quality.window_end)}
                      </p>
                    </div>
                    <div>
                      <p className="text-slate-500">Samples</p>
                      <p className="text-slate-800 font-medium">{section.data_quality.n_samples}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Last Event</p>
                      <p className="text-slate-800 font-medium">{formatTimestamp(section.data_quality.last_event_ts)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Age of Last Event</p>
                      <p className="text-slate-800 font-medium">{formatDuration(section.data_quality.age_of_last_event_seconds)}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-slate-500">Freshness Status</p>
                      <p className="text-slate-800 font-medium">{section.data_quality.data_freshness_status}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 px-6 py-4 flex justify-between items-center">
          <div className="text-xs text-slate-500">
            <p><strong>Classification:</strong> {report.classification} | <strong>PII Risk:</strong> {report.pii_risk}</p>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-brand-blue text-white rounded-md hover:bg-brand-blue/90 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemHealthReportViewer;

